// Back-office editor for an org's ResiGen valuation tables (super-user only — this is negotiated
// pricing). Edits the solar/battery tier grids and the tax-equity contribution row; "Reset" reverts
// the org to the Luminia defaults (removes the override).

import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { Button } from '/imports/ui/Button';
import { Modal, ModalStyled } from '/imports/ui/Modal';
import { toastOnError } from '/imports/ui/utils';
import {
  ResigenTables, ResigenValuationTier, resigenDefaultTables, resigenSaveOrgTables,
} from '/imports/api/resigenProjects';

const TablesModalStyled = styled(ModalStyled)`
  .modal-window { width: 46rem; max-width: 100%; }
  .section-title { font-weight: 600; font-size: 0.85rem; color: ${css.grey2}; margin: 0.9rem 0 0.25rem; }
  table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
  th, td { text-align: right; padding: 0.2rem 0.3rem; }
  th { color: ${css.grey4}; font-weight: 600; }
  input { width: 6rem; text-align: right; padding: 0.2rem 0.4rem; border: ${css.inputBorder}; border-radius: ${css.inputBorderRadius}; }
  .row-del { color: ${css.grey4}; cursor: pointer; }
  .add-row { margin-top: 0.25rem; }
  .modal-buttons { display: flex; gap: 1rem; justify-content: flex-end; margin-top: 1.25rem; }
  .reset { margin-right: auto; }
`;

const clone = (t: ResigenTables): ResigenTables => JSON.parse(JSON.stringify(t));

/** One editable tier grid (solar $/W or battery $/kWh). */
const TierGrid = (props: { unit: string; tiers: ResigenValuationTier[]; onChange: (tiers: ResigenValuationTier[]) => void }) => {
  const { unit, tiers, onChange } = props;
  const patch = (i: number, field: keyof ResigenValuationTier, value: string) => {
    const next = tiers.map((t, j) => (j === i ? { ...t, [field]: Number(value) || 0 } : t));
    onChange(next);
  };
  return (
    <>
      <table>
        <thead>
          <tr><th>{strings.Min()}</th><th>{strings.Max()}</th><th>{strings.Standard()} ({unit})</th><th>EC / DC</th><th>EC + DC</th><th></th></tr>
        </thead>
        <tbody>
          {
            tiers.map((t, i) => (
              <tr key={i}>
                {(['minKw', 'maxKw', 'standard', 'ecOrDc', 'ecPlusDc'] as const).map((f) => (
                  <td key={f}><input type="number" step="any" value={t[f]} onChange={(e) => patch(i, f, e.target.value)} /></td>
                ))}
                <td><span className="row-del" onClick={() => onChange(tiers.filter((_, j) => j !== i))}>✕</span></td>
              </tr>
            ))
          }
        </tbody>
      </table>
      <Button className="secondary compact add-row" onClick={() => onChange([...tiers, { minKw: tiers[tiers.length - 1]?.maxKw || 0, maxKw: 0, standard: 0, ecOrDc: 0, ecPlusDc: 0 }])}>{strings.Add()}</Button>
    </>
  );
};

/** Super-user modal editing one org's ResiGen tables. */
export const ResigenTablesModal = (props: { orgId: string; current?: ResigenTables; onClose: () => void }) => {
  const { orgId, onClose } = props;
  const [tables, setTables] = useState<ResigenTables>(() => clone(props.current || resigenDefaultTables));
  const save = toastOnError(async () => {
    await resigenSaveOrgTables.call({ orgId, tables });
    toast.success(strings.Done());
    onClose();
  });
  const reset = toastOnError(async () => {
    await resigenSaveOrgTables.call({ orgId });
    toast.success(strings.ResetToDefaults());
    onClose();
  });
  return (
    <Modal styled={TablesModalStyled} title={`${strings.ResiGen()} — ${strings.ValuationTables()}`} onClose={onClose}>
      <div className="section-title">{strings.SolarValuation()} ($/W DC)</div>
      <TierGrid unit="$/W" tiers={tables.solarTiers} onChange={(solarTiers) => setTables({ ...tables, solarTiers })} />
      <div className="section-title">{strings.BatteryValuation()} ($/kWh)</div>
      <TierGrid unit="$/kWh" tiers={tables.batteryTiers} onChange={(batteryTiers) => setTables({ ...tables, batteryTiers })} />
      <div className="section-title">{strings.TaxEquityContribution()} (%)</div>
      <table>
        <thead><tr><th>{strings.Standard()}</th><th>EC / DC</th><th>EC + DC</th></tr></thead>
        <tbody>
          <tr>
            {(['standard', 'ecOrDc', 'ecPlusDc'] as const).map((f) => (
              <td key={f}>
                <input
                  type="number" step="any" value={Math.round(tables.contribution[f] * 10000) / 100}
                  onChange={(e) => setTables({ ...tables, contribution: { ...tables.contribution, [f]: (Number(e.target.value) || 0) / 100 } })}
                />
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      <div className="modal-buttons">
        <Button className="secondary reset" onClick={() => reset()}>{strings.ResetToDefaults()}</Button>
        <Button className="secondary" onClick={onClose}>{strings.Cancel()}</Button>
        <Button className="primary" onClick={() => save()}>{strings.Save()}</Button>
      </div>
    </Modal>
  );
};
